import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { createAudioTranscription, type AudioLanguage } from "../../../actions/audio-transcription/createAudioTranscription";
import { useAudioLanguagePreference } from "../../../hooks/useAudioLanguagePreference";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import TransformPanel from "../../../components/transform/TransformPanel";
import { useRecordingGuard } from "../../../hooks/useRecordingGuard";
import { useRecordingGuardContext } from "../../../contexts/RecordingGuardContext";
import { useUploadForm } from "../../../hooks/useUploadForm";
import { useTransform } from "../../../hooks/useTransform";
import {
  LEGAL_VOCAB_PRESET,
  SHOW_VOCABULARY_PANEL,
  scribePrimaryCtaContainerSx,
  scribeResetButtonSx,
} from "../../../constants/scribeUi";
import AudioTranscriptionList from "../../audio-transcription/AudioTranscriptionList";
import GlassPanel from "../GlassPanel";
import RecoveredRecordings from "./RecoveredRecordings";
import {
  HEARTBEAT_INTERVAL_MS,
  LIVE_SESSION_WINDOW_MS,
  RECORDING_TIMESLICE_MS,
  appendChunk,
  assemble,
  createSession,
  deleteSession,
  extensionForMimeType,
  listUnfinished,
  markStopped,
  markSubmitted,
  purgeStale,
  touchSession,
  type RecordingSession,
} from "../../../lib/recordingStore";

const MAX_RECORDING_DURATION_S = 90 * 60;
const MAX_AUDIO_UPLOAD_SIZE_MB = 100;
const BYTES_PER_MB = 1024 * 1024;

const parseSpeakerNames = (input: string): Record<string, string> | undefined => {
  const entries: Array<[string, string]> = [];
  for (const rawLine of input.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(/^speaker\s*(\d+)\s*[:=-]\s*(.+)$/i);
    if (!match) continue;
    entries.push([`Speaker ${match[1]}`, match[2].trim()]);
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/** WebM where the browser can record it (Chrome, Edge, Firefox), otherwise MP4 (Safari). */
const pickRecordingMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return "audio/webm";
  return ["audio/webm", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported(type));
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const fileStamp = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}_${pad2(d.getHours())}-${pad2(d.getMinutes())}`;
};

interface RecordTabProps {
  isApproved: boolean;
  /** Reports whether a recording is live (started and not yet stopped, paused included). */
  onRecordingChange?: (active: boolean) => void;
  /**
   * Whether this tab is the one on screen. The tab stays mounted while hidden (so a live
   * recording survives a tab switch), so it uses this to refresh the saved language and
   * the Recent Transcriptions list when it becomes visible again.
   */
  active?: boolean;
}

const RecordTab: React.FC<RecordTabProps> = ({ isApproved, onRecordingChange, active = true }) => {
  const uploadForm = useUploadForm({ minFiles: 1, maxFiles: 1, maxTotalSizeMB: 200, maxSingleFileSizeMB: 200 });
  const transform = useTransform();
  const [downloadTarget, setDownloadTarget] = useState("");
  const [originalDownloadTarget, setOriginalDownloadTarget] = useState("");

  useEffect(() => {
    if (transform.transformDownloadKey) {
      setDownloadTarget(transform.transformDownloadKey);
    } else if (originalDownloadTarget) {
      setDownloadTarget(originalDownloadTarget);
    }
  }, [transform.transformDownloadKey, originalDownloadTarget]);

  const [dictationMode, setDictationMode] = useState(true);
  const [aiCorrection, setAiCorrection] = useState(true);
  const [speakerIdentification, setSpeakerIdentification] = useState(false);
  const [numSpeakers, setNumSpeakers] = useState("auto");
  const [speakerNamesText, setSpeakerNamesText] = useState("");
  const [vocabulary, setVocabulary] = useState("");
  // True from the moment a recording starts until its clip is submitted or cleared, so the
  // language that recording started with cannot be rewritten underneath it. It is state
  // set at start (not derived from isRecording / audioBlob) because the recorder hands over
  // the clip asynchronously after stop, which would leave a window with neither set.
  const [clipPending, setClipPending] = useState(false);
  const { language, setLanguage, reload: reloadLanguage } = useAudioLanguagePreference({ locked: clipPending });
  const speakerNames = useMemo(() => parseSpeakerNames(speakerNamesText), [speakerNamesText]);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Browser-side copy of the recording in progress (null when browser storage is unavailable).
  const sessionIdRef = useRef<string | null>(null);
  // Seconds recorded so far (pauses excluded), readable from recorder callbacks.
  const durationRef = useRef(0);
  // The saved session whose audio is being submitted; finalised once the submit succeeds.
  const submittingSessionRef = useRef<string | null>(null);
  // Sessions created by this mount, never offered back as "found" recordings.
  const ownSessionIdsRef = useRef(new Set<string>());
  // Recovered recordings discarded or submitted during this mount: a listing that was already
  // under way when that happened must not bring their cards back.
  const removedIdsRef = useRef(new Set<string>());
  // True from a Start click until the recorder is running (or has failed), so a second click
  // while the microphone prompt or the browser copy is pending cannot start a second recorder.
  const startingRef = useRef(false);
  const [starting, setStarting] = useState(false);
  // Keeps this tab's open session marked as in use for other tabs (see touchSession).
  const heartbeatRef = useRef<{ id: string; timer: ReturnType<typeof setInterval> } | null>(null);
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current.timer);
    heartbeatRef.current = null;
  }, []);
  const startHeartbeat = useCallback(
    (id: string) => {
      stopHeartbeat();
      heartbeatRef.current = { id, timer: setInterval(() => void touchSession(id), HEARTBEAT_INTERVAL_MS) };
    },
    [stopHeartbeat],
  );
  const ownerIdRef = useRef("");
  ownerIdRef.current = uploadForm.userId;
  const [recoverable, setRecoverable] = useState<RecordingSession[]>([]);
  const [recoveryError, setRecoveryError] = useState("");
  // The recovered recording whose submit is under way. The ref is set synchronously so a
  // double click cannot start a second submit; the state drives the disabled buttons.
  const busyRecoveredRef = useRef<string | null>(null);
  const [busyRecoveredId, setBusyRecoveredId] = useState<string | null>(null);
  const setBusyRecovered = useCallback((id: string | null) => {
    busyRecoveredRef.current = id;
    setBusyRecoveredId(id);
  }, []);
  // False once the tab has unmounted, so late async work (a pending microphone permission,
  // a recorder's stop event) never creates anything nothing will clean up.
  const mountedRef = useRef(true);

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  // While hidden, other tabs may change the saved language or add transcriptions. Refresh
  // both once this tab is visible again. Nothing is refreshed while a recording is live or
  // its clip is waiting to be submitted: the clip keeps the language it was recorded with
  // (a language changed on the Audio tab does not apply to it). The held-back refresh runs
  // once the clip is submitted or cleared.
  const staleWhileHiddenRef = useRef(false);
  const { setRefresh } = uploadForm;
  useEffect(() => {
    if (!active) {
      staleWhileHiddenRef.current = true;
      return;
    }
    if (clipPending || !staleWhileHiddenRef.current) return;
    staleWhileHiddenRef.current = false;
    reloadLanguage();
    setRefresh(true);
  }, [active, clipPending, reloadLanguage, setRefresh]);

  // The duration timer belongs to the recording lifecycle (start, resume, pause, stop,
  // release, unmount), never to the clip's object URL.
  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      durationRef.current += 1;
      setRecordingDuration((d) => d + 1);
    }, 1000);
  }, [clearTimer]);

  const startRecording = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current) {
        // Left the page while the permission prompt was open: nothing owns this stream.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const pickedMimeType = pickRecordingMimeType();
      const recorder = pickedMimeType ? new MediaRecorder(stream, { mimeType: pickedMimeType }) : new MediaRecorder(stream);
      const mimeType = pickedMimeType ?? (recorder.mimeType || "audio/webm");
      // No saved copy until the signed-in user is known (a recording nobody owns could never be
      // offered back), and none when browser storage is slow: createSession gives up after a
      // couple of seconds. Either way the recording itself goes ahead in memory, as before.
      const sessionId = ownerIdRef.current ? await createSession({ mimeType, ownerId: ownerIdRef.current }) : null;
      if (sessionId) ownSessionIdsRef.current.add(sessionId);
      if (!mountedRef.current) {
        // Left the page while the browser copy was being created: nothing recorded yet.
        stream.getTracks().forEach((t) => t.stop());
        if (sessionId) void deleteSession(sessionId);
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];
      sessionIdRef.current = sessionId;
      if (sessionId) startHeartbeat(sessionId);
      else stopHeartbeat();
      durationRef.current = 0;
      let seq = 0;

      recorder.ondataavailable = (e) => {
        // Every chunk is kept, in order, even an empty one: only the first chunk carries the
        // container header, so a recording with any chunk missing can be undecodable.
        chunksRef.current.push(e.data);
        const chunkSeq = seq++;
        if (sessionId) void appendChunk(sessionId, chunkSeq, e.data);
      };

      recorder.onstop = () => {
        // The recorder can stop by itself (microphone unplugged, track ended), so the timer
        // must stop with it: the 90 minute auto-stop would otherwise call stop() on it again.
        clearTimer();
        // Saved before the unmount check below: a recording released by Leave Page or
        // sign-out must stay recoverable.
        if (sessionId) void markStopped(sessionId, durationRef.current * 1000);
        stream.getTracks().forEach((t) => t.stop());
        if (streamRef.current === stream) streamRef.current = null;
        // Released for a leave: the page is gone, so there is no clip to hold and an object
        // URL created now would never be revoked.
        if (!mountedRef.current) return;
        // A recorder that stops by itself (every track ended) never went through Stop, so the
        // controls, the leave-page guard and the parent's recording flag are reset here. Only
        // for the current recorder: a stop event from an earlier one must not end a newer
        // recording. Stop and Leave Page set the same flags, which is a no-op for React.
        if (mediaRecorderRef.current === recorder) {
          setIsRecording(false);
          setIsPaused(false);
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      mediaRecorderRef.current = recorder;
      // A timeslice makes the recorder hand over data every few seconds so each chunk can be
      // saved to the browser (see recordingStore). Only the FIRST chunk carries the EBML/WebM
      // init header; if it is ever dropped the assembled file is headerless and undecodable
      // by every engine (Whisper/ffmpeg and Amazon Transcribe both reject it), which broke
      // ~half of prod recordings on 2026-05-29. That is safe here because ondataavailable
      // never skips or filters a chunk: chunksRef and the saved copy both keep all of them,
      // in order, including the first.
      recorder.start(RECORDING_TIMESLICE_MS);
      setIsRecording(true);
      setClipPending(true);
      setRecordingDuration(0);
      setAudioBlob(null);
      setAudioUrl(null);
      uploadForm.setFileError("");

      startTimer();
    } catch {
      /* mic permission denied */
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }, [uploadForm, startTimer, clearTimer, startHeartbeat, stopHeartbeat]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
    setIsRecording(false);
    setIsPaused(false);
    clearTimer();
  }, [clearTimer]);

  // Stops the recorder and releases the microphone straight away (every track, not only
  // when the recorder's async `stop` event fires), so the browser's mic indicator goes off
  // as the user leaves the page.
  const releaseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    clearTimer();
  }, [clearTimer]);

  // Native "Leave site?" prompt on refresh or tab close, and the in-page dialog for the
  // Navbar links and Sign out (the provider sits above the Navbar and this tab).
  useRecordingGuard(isRecording);
  const { report: reportRecordingGuard } = useRecordingGuardContext();
  useEffect(() => {
    reportRecordingGuard(isRecording, releaseRecording);
    return () => reportRecordingGuard(false, null);
  }, [isRecording, releaseRecording, reportRecordingGuard]);

  // Unmounting (route change, sign-out) must never leave the microphone open.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopHeartbeat();
      clearTimer();
      const recorder = mediaRecorderRef.current;
      // onstop stays attached: it saves the session as stopped, then returns early because
      // the tab is gone, so no clip or object URL is created.
      if (recorder && recorder.state !== "inactive") recorder.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [stopHeartbeat, clearTimer]);

  // The recorder throws if asked to pause or resume from the wrong state (it may have stopped
  // by itself a moment before the controls re-render), so each acts only from its own state.
  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== "recording") return;
    recorder.pause();
    setIsPaused(true);
    clearTimer();
  }, [clearTimer]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== "paused") return;
    recorder.resume();
    setIsPaused(false);
    startTimer();
  }, [startTimer]);

  // Only the clip's object URL is released here. The audioUrl changes when a new recording
  // starts (blob URL to null) while its timer is already running, so clearing the timer in
  // this cleanup stopped the new recording's duration and its 90 minute auto-stop (AIS-31).
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  useEffect(() => {
    if (isRecording && recordingDuration >= MAX_RECORDING_DURATION_S) {
      stopRecording();
    }
  }, [isRecording, recordingDuration, stopRecording]);

  // The one submit path, for a fresh recording and a recovered one alike.
  const submitRecording = (blob: Blob, sessionId: string | null, mimeType: string): boolean => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const file = new File([blob], `recording_${ts}.${extensionForMimeType(mimeType)}`, { type: mimeType });
    if (file.size > MAX_AUDIO_UPLOAD_SIZE_MB * BYTES_PER_MB) {
      uploadForm.setFileError(`Recording is ${(file.size / BYTES_PER_MB).toFixed(1)} MB. Maximum allowed per file is ${MAX_AUDIO_UPLOAD_SIZE_MB} MB.`);
      return false;
    }

    uploadForm.setFileError("");
    submittingSessionRef.current = sessionId;
    uploadForm.setFiles([file]);
    uploadForm.setProcessing(true);
    uploadForm.setShowLoader(true);
    return true;
  };

  const handleTranscribe = () => {
    if (!audioBlob) return;
    submitRecording(audioBlob, sessionIdRef.current, audioBlob.type || "audio/webm");
  };

  // After a successful submit the browser copy is no longer needed. A failed submit never
  // gets here, so the copy stays recoverable.
  const finalizeSession = useCallback(async (id: string) => {
    // The job exists, so the card is finished with whatever happens to the browser copy.
    removedIdsRef.current.add(id);
    setRecoverable((prev) => prev.filter((s) => s.id !== id));
    await markSubmitted(id);
    // Once more if the first attempt failed; the next visit's clean-up catches anything left.
    if (!(await deleteSession(id))) await deleteSession(id);
  }, []);

  useEffect(() => {
    // Recordings from before this mount (a refresh, a closed tab, a crash, a released Leave
    // Page). Waits for the signed-in user so another user on this browser never sees them.
    const ownerId = uploadForm.userId;
    // Never keep another user's list on screen while the id changes or is unknown.
    setRecoverable([]);
    if (!ownerId) return;
    let cancelled = false;
    const load = async () => {
      const sessions = (await listUnfinished(ownerId)) ?? [];
      if (cancelled) return;
      setRecoverable(sessions.filter((s) => !ownSessionIdsRef.current.has(s.id) && !removedIdsRef.current.has(s.id)));
    };
    void (async () => {
      await purgeStale();
      await load();
    })();
    // A recording cut off by this refresh was written to a moment ago, so it still looks live
    // to the first listing. Look once more after that window has passed.
    const recheck = setTimeout(() => void load(), LIVE_SESSION_WINDOW_MS + 1_000);
    return () => {
      cancelled = true;
      clearTimeout(recheck);
    };
  }, [uploadForm.userId]);

  // The recovered recording's submit is over (finished or failed): release its buttons.
  const sawProcessingRef = useRef(false);
  useEffect(() => {
    if (uploadForm.processing) {
      sawProcessingRef.current = true;
    } else if (sawProcessingRef.current) {
      sawProcessingRef.current = false;
      setBusyRecovered(null);
    }
  }, [uploadForm.processing, setBusyRecovered]);

  const handleRecoveredTranscribe = async (session: RecordingSession) => {
    if (busyRecoveredRef.current) return;
    setBusyRecovered(session.id);
    setRecoveryError("");
    try {
      const submitted = submitRecording(await assemble(session.id), session.id, session.mimeType);
      if (!submitted) setBusyRecovered(null);
    } catch {
      setBusyRecovered(null);
      setRecoveryError("We couldn't read that recording from this browser. Try Download, or discard it.");
    }
  };

  const handleRecoveredDownload = async (session: RecordingSession) => {
    setRecoveryError("");
    try {
      const url = URL.createObjectURL(await assemble(session.id));
      const link = document.createElement("a");
      link.href = url;
      link.download = `recording-${fileStamp(session.startedAt)}.${extensionForMimeType(session.mimeType)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL?.(url), 10_000);
    } catch {
      setRecoveryError("We couldn't read that recording from this browser, so it can't be downloaded.");
    }
  };

  const handleRecoveredDiscard = async (session: RecordingSession) => {
    // The upload of this recording is under way: the browser copy is its only backup.
    if (busyRecoveredRef.current === session.id) return;
    setRecoveryError("");
    if (await deleteSession(session.id)) {
      removedIdsRef.current.add(session.id);
      setRecoverable((prev) => prev.filter((s) => s.id !== session.id));
    } else {
      setRecoveryError("We couldn't delete that recording from this browser. Please try again.");
    }
  };

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (fileKeys, filePasswords) =>
      createAudioTranscription(fileKeys, {
        dictationMode,
        aiCorrection,
        speakerIdentification,
        numSpeakers: speakerIdentification && numSpeakers !== "auto" ? Number(numSpeakers) || undefined : undefined,
        speakerNames,
        vocabulary: SHOW_VOCABULARY_PANEL ? vocabulary : LEGAL_VOCAB_PRESET,
        filePasswords,
        enableDocumentChat: uploadForm.enableDocumentChat,
        language,
      }),
    () => {
      const submitted = submittingSessionRef.current;
      submittingSessionRef.current = null;
      if (submitted && submitted === heartbeatRef.current?.id) stopHeartbeat();
      setBusyRecovered(null);
      if (submitted) void finalizeSession(submitted);
      setAudioBlob(null);
      setClipPending(false);
      setSpeakerNamesText("");
      setVocabulary("");
      setSpeakerIdentification(false);
      setNumSpeakers("auto");
    },
  );

  const fmtDuration = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // #402 — start a fresh recording without refreshing the page. Clears the
  // finished transcript, the recorded clip (revoking its object URL), and job
  // state (handleModalClose also refreshes the Recent Transcriptions list),
  // returning the tab to a clean "Start recording" state.
  //
  // #611 — this used `transform.reset()`, which is the transform-UNDO primitive:
  // it restores displayText from originalText and never clears the source, so
  // `hasText` stayed true and the previous transcript (plus its whole transform
  // toolbar) survived the reset. `setSourceText("")` clears both, which is what
  // "start fresh" actually means.
  const handleNewRecording = useCallback(() => {
    stopHeartbeat();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setClipPending(false);
    setRecordingDuration(0);
    setIsRecording(false);
    setIsPaused(false);
    transform.setSourceText("");
    uploadForm.handleModalClose();
    setDownloadTarget("");
    setOriginalDownloadTarget("");
  }, [audioUrl, transform, uploadForm, stopHeartbeat]);

  // A result is on screen once the transcript has streamed in (or a job completed).
  const hasResult = transform.hasText || (!uploadForm.showLoader && !!uploadForm.jobId);

  return (
    <>
      <RecoveredRecordings
        sessions={recoverable}
        transcribeDisabled={!isApproved || isRecording || !!audioBlob || uploadForm.processing}
        busySessionId={busyRecoveredId}
        error={recoveryError}
        onTranscribe={handleRecoveredTranscribe}
        onDownload={handleRecoveredDownload}
        onDiscard={handleRecoveredDiscard}
      />
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 1,
        }}
      >
        Record &amp; Transcribe
      </Typography>

      <GlassPanel>
        {hasResult && (
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleNewRecording}
              disabled={uploadForm.processing}
              sx={scribeResetButtonSx}
            >
              ＋ New recording
            </Button>
          </Box>
        )}
        <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "center" }}>
          <TextField
            select
            label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value as AudioLanguage)}
            size="small"
            sx={{ width: 180 }}
            inputProps={{ "aria-label": "Audio language" }}
          >
            <MenuItem value="af">Afrikaans</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: "flex", gap: 3, mb: 1.5, flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Switch checked={dictationMode} onChange={(e) => setDictationMode(e.target.checked)} />}
            label="Dictation Mode"
            sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
          />
          <FormControlLabel
            control={<Switch checked={aiCorrection} onChange={(e) => setAiCorrection(e.target.checked)} />}
            label="AI Correction"
            sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={speakerIdentification}
                onChange={(e) => setSpeakerIdentification(e.target.checked)}
              />
            }
            label="Identify Speakers"
            sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
          />
        </Box>

        <Collapse in={speakerIdentification}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              select
              label="Number of speakers"
              value={numSpeakers}
              onChange={(e) => setNumSpeakers(e.target.value)}
              size="small"
              sx={{ width: 200 }}
            >
              <MenuItem value="auto">Auto-detect</MenuItem>
              {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
                <MenuItem key={n} value={String(n)}>{n} speakers</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Speaker names (optional)"
              value={speakerNamesText}
              onChange={(e) => setSpeakerNamesText(e.target.value)}
              placeholder={"Speaker 1: Attorney\nSpeaker 2: Client"}
              helperText="One per line: Speaker 1: Name"
              size="small"
            />
          </Box>
        </Collapse>

        {SHOW_VOCABULARY_PANEL && (
          <Accordion
            sx={{
              mt: 1,
              mb: 1.5,
              backgroundColor: "var(--s2)",
              border: "1px solid var(--border)",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--t2)" }} />}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "var(--t2)" }}>
                📘 Vocabulary &amp; Term Guide
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "var(--s1)", borderTop: "1px solid var(--border)" }}>
              <Typography sx={{ fontSize: "0.78rem", color: "var(--t2)", mb: 1 }}>
                Add names, terms, or jargon that AI-SCRIBE should spell correctly. Comma-separated.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={vocabulary}
                  onChange={(e) => setVocabulary(e.target.value)}
                  placeholder="Enter names, acronyms, technical terms…"
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const current = vocabulary.trim();
                    setVocabulary(current ? `${current}, ${LEGAL_VOCAB_PRESET}` : LEGAL_VOCAB_PRESET);
                  }}
                  sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
                >
                  ⚖️ Legal
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          {!isRecording ? (
            <Box sx={scribePrimaryCtaContainerSx}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<FiberManualRecordIcon />}
                onClick={startRecording}
                disabled={starting}
              >
                Start recording
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              {isPaused ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={resumeRecording}
                  sx={{ minWidth: { xs: "100%", sm: 160 } }}
                >
                  Resume
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  startIcon={<PauseIcon />}
                  onClick={pauseRecording}
                  sx={{ minWidth: { xs: "100%", sm: 140 } }}
                >
                  Pause
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="large"
                startIcon={<StopIcon />}
                onClick={stopRecording}
                sx={{ minWidth: { xs: "100%", sm: 200 } }}
              >
                Stop ({fmtDuration(recordingDuration)})
              </Button>
              {isPaused && (
                <Typography
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    fontSize: "0.76rem",
                    color: "var(--warn)",
                    fontWeight: 500,
                  }}
                >
                  Paused
                </Typography>
              )}
            </Box>
          )}

          <Typography sx={{ fontSize: "0.74rem", color: "var(--t4)", textAlign: "center" }}>
            {isRecording
              ? "Pause if interrupted — Stop when done."
              : "Start — speak — Pause if interrupted — Stop when done."}
            {" "}Auto-stops after 90 min.
          </Typography>
        </Box>

        {uploadForm.fileError && (
          <Typography color="error" sx={{ mb: 2, fontSize: "0.82rem" }}>
            {uploadForm.fileError}
          </Typography>
        )}

        {audioUrl && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                background: "var(--s2)",
                border: "1px solid var(--border-hi)",
                borderRadius: "14px",
                mb: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: isPaused ? "var(--warn)" : "var(--teal)",
                  boxShadow: isPaused ? "none" : "0 0 8px var(--teal-glow)",
                  animation: isPaused ? "none" : "dotPulse 2s ease-in-out infinite",
                }}
              />
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.78rem", flex: 1 }}>
                {isPaused ? "recording paused" : "microphone recording"}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "var(--t3)" }}>
                {audioBlob ? `${(audioBlob.size / 1024).toFixed(0)} KB` : ""}
              </Typography>
            </Box>
            <audio src={audioUrl} controls style={{ width: "100%", height: 40 }} />
          </Box>
        )}

        {audioBlob && (
          <Box sx={scribePrimaryCtaContainerSx}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={!isApproved || uploadForm.processing}
              onClick={handleTranscribe}
            >
              ⚡ Transcribe Recording
            </Button>
          </Box>
        )}

        {!audioBlob && !isRecording && (
          <Typography sx={{ textAlign: "center", color: "var(--t4)", fontSize: "0.78rem", mt: 1 }}>
            No recording yet.
          </Typography>
        )}
        </Box>
        <InlineProcessingStatus
          compact
          open={uploadForm.showLoader}
          files={uploadForm.files}
          jobId={uploadForm.jobId}
          userId={uploadForm.userId}
          onUpload={uploadHandler}
          onComplete={async () => {
            if (uploadForm.jobId) await uploadForm.checkDocumentChatStatus(uploadForm.jobId);
            uploadForm.setProcessing(false);
          }}
          onTextExtracted={transform.streamSourceText}
          onDownloadReady={(key: string) => { setDownloadTarget(key); setOriginalDownloadTarget(key); }}
          onClose={uploadForm.handleModalClose}
          chatEnabled={uploadForm.documentChatStatus?.chatEnabled}
          hasDocumentChatAccess={uploadForm.hasDocumentChatAccess}
          documentTitle="Recording Transcription"
        />
        <TransformPanel
          displayText={transform.displayText}
          hasText={transform.hasText}
          isTransforming={transform.isTransforming}
          isStreamingResult={transform.isStreamingResult}
          isSourceStreaming={transform.isSourceStreaming}
          transformError={transform.transformError}
          downloadUrl={downloadTarget}
          inlineChatJobId={uploadForm.jobId}
          inlineChatFileName="Recording Transcription"
          onTransform={transform.runTransform}
          onReset={transform.reset}
        />
      </GlassPanel>

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--teal)",
            mb: 1,
          }}
        >
          Recent Transcriptions
        </Typography>
        <GlassPanel>
          <AudioTranscriptionList
            refresh={uploadForm.refresh}
            onRefreshComplete={uploadForm.handleRefreshComplete}
          />
        </GlassPanel>
      </Box>
    </>
  );
};

export default RecordTab;
